import React, { MutableRefObject, ReactElement, useEffect } from 'react';
import { CanvasUiColorTheme, ThemeSettings, ColorThemeValues } from 'src/editor/content-creation/types';
import { RefreshLightIcon } from 'src/monet/icons';
import { ControlPanelActionButton } from '../../control-panel/action-button';
import { ControlPanelToggleButton } from '../../control-panel/toggle-button';
import { trackSegmentEvent } from 'src/utils/segment';
import {
  DEFAULT_THEME_COLOR_VALUES,
  getColorThemeValuesFromColor,
  getDefaultThemeSettings,
} from 'src/editor/content-creation/helpers/themes/get-theme-properties';
import { useEditorStore } from 'src/editor/content-creation/editor-store';
import classNames from 'classnames';
import { useClickAway, useMount } from 'react-use';
import { Nullable } from 'src/types/nullable.type';
import { easings, useTransition, animated } from '@react-spring/web';
import { ColorPicker } from './color-picker';

type CustomValueKey = keyof ColorThemeValues;
type ColorPickerTypeKey = keyof ColorThemeValues | 'brand';

const ColorSettingInput = ({
  label,
  value,
  bold = false,
  onClick,
  showInput = false,
}: {
  showInput?: boolean;
  label: string;
  bold?: boolean;
  value: string;
  onClick: () => void;
}) => {
  if (!showInput) {
    return (
      <div className='flex gap-4 justify-between items-center cursor-pointer' onClick={onClick}>
        <div
          className={classNames('flex-1 truncate', {
            'text-neue-journey-medium-strong': bold,
            'text-neue-journey-medium': !bold,
          })}
        >
          {label}
        </div>
        <div className='relative w-4 h-4'>
          <div className='absolute pointer-events-none -inset-[1px] rounded-full border border-neue-journey-fg-20' />
          <div className='absolute pointer-events-none inset-0 rounded-full' style={{ backgroundColor: value }} />
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-2 justify-between cursor-pointer' onClick={onClick}>
      <div
        className={classNames('flex-1 truncate', {
          'text-neue-journey-medium-strong': bold,
          'text-neue-journey-medium': !bold,
        })}
      >
        {label}
      </div>
      <div className='flex items-center justify-between py-[9px] px-3 rounded-md bg-neue-journey-fg-10'>
        <span className='text-neue-journey-medium text-neue-journey-fg-50'>{value.toUpperCase()}</span>
        <div
          className='h-[18px] w-[18px] shrink-0 rounded-full border border-bedrock-white'
          style={{ backgroundColor: value }}
        />
      </div>
    </div>
  );
};

const THEME_ICON_SVGS: Record<Exclude<CanvasUiColorTheme, 'default'>, ReactElement> = {
  light: (
    <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <g clipPath='url(#clip0_208_2399)'>
        <path
          d='M10 13.25C11.7949 13.25 13.25 11.7949 13.25 10C13.25 8.20507 11.7949 6.75 10 6.75C8.20507 6.75 6.75 8.20507 6.75 10C6.75 11.7949 8.20507 13.25 10 13.25Z'
          stroke='currentColor'
          strokeWidth='1.1'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path d='M10 0.75V2.25' stroke='currentColor' strokeWidth='1.1' strokeLinecap='round' strokeLinejoin='round' />
        <path
          d='M15.25 4.75L14.0659 5.93416'
          stroke='currentColor'
          strokeWidth='1.1'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M19.25 10H17.75'
          stroke='currentColor'
          strokeWidth='1.1'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M15.25 15.2505L14.0659 14.0664'
          stroke='currentColor'
          strokeWidth='1.1'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M10 17.75V19.25'
          stroke='currentColor'
          strokeWidth='1.1'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M5.93414 14.0664L4.75 15.2505'
          stroke='currentColor'
          strokeWidth='1.1'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path d='M2.25 10H0.75' stroke='currentColor' strokeWidth='1.1' strokeLinecap='round' strokeLinejoin='round' />
        <path
          d='M5.93402 5.9342L4.74988 4.75'
          stroke='currentColor'
          strokeWidth='1.1'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </g>
      <defs>
        <clipPath id='clip0_208_2399'>
          <rect width='20' height='20' fill='white' />
        </clipPath>
      </defs>
    </svg>
  ),
  dark: (
    <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M16.25 13.0314C15.7575 13.1436 15.2459 13.2027 14.7209 13.2027C10.8082 13.2027 7.63607 9.9185 7.63607 5.86709C7.63607 4.75253 7.87614 3.69603 8.3057 2.75C5.12795 3.47387 2.75 6.40659 2.75 9.9143C2.75 13.9657 5.9221 17.25 9.8348 17.25C12.6711 17.25 15.1182 15.5242 16.25 13.0314Z'
        stroke='currentColor'
        strokeWidth='1.1'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  ),
  bright: (
    <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M10 2.75C6.5 2.75 4.75 5.5 4.75 8C4.75 12 7.75 12.5 7.75 14V16.2505C7.75 16.8028 8.1977 17.25 8.75 17.25H11.25C11.8023 17.25 12.25 16.8028 12.25 16.2505V14C12.25 12.5 15.25 12 15.25 8C15.25 5.5 13.5 2.75 10 2.75Z'
        stroke='currentColor'
        strokeWidth='1.1'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path d='M8 14.75H12' stroke='currentColor' strokeWidth='1.1' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
  ),
  custom: (
    <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M5.75 4.5V5.25C5.75 5.51522 5.85536 5.76957 6.04289 5.95711C6.23043 6.14464 6.48478 6.25 6.75 6.25H15.25C15.5152 6.25 15.7696 6.14464 15.9571 5.95711C16.1446 5.76957 16.25 5.51522 16.25 5.25V3.75C16.25 3.48478 16.1446 3.23043 15.9571 3.04289C15.7696 2.85536 15.5152 2.75 15.25 2.75H6.75C6.48478 2.75 6.23043 2.85536 6.04289 3.04289C5.85536 3.23043 5.75 3.48478 5.75 3.75V4.5ZM5.75 4.5H4.75C4.48478 4.5 4.23043 4.60536 4.04289 4.79289C3.85536 4.98043 3.75 5.23478 3.75 5.5V8.75C3.75 9.01522 3.85536 9.26957 4.04289 9.45711C4.23043 9.64464 4.48478 9.75 4.75 9.75H8C8.53043 9.75 9.03914 9.96071 9.41421 10.3358C9.78929 10.7109 10 11.2196 10 11.75V13.75M10 13.75H9C8.73478 13.75 8.48043 13.8554 8.29289 14.0429C8.10536 14.2304 8 14.4848 8 14.75V17.25M10 13.75H11C11.2652 13.75 11.5196 13.8554 11.7071 14.0429C11.8946 14.2304 12 14.4848 12 14.75V17.25'
        stroke='currentColor'
        strokeWidth='1.1'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  ),
};

const FLOAT_ANIMATION_CONFIG = {
  mass: 1,
  tension: 400,
  friction: 40,
};

export const ThemeColorsContent = ({
  themeSettings,
  closeRef,
  panelContentRef,
}: {
  closeRef: MutableRefObject<() => void>;
  themeSettings: ThemeSettings;
  panelContentRef: MutableRefObject<Nullable<HTMLDivElement>>;
}) => {
  const [openColorPicker, setOpenColorPicker] = React.useState<Nullable<ColorPickerTypeKey>>(null);
  const colorPickerRef = React.useRef<HTMLDivElement>(null);
  const dispatchUserEditorAction = useEditorStore((state) => state.dispatchUserEditorAction);

  useMount(() => {
    closeRef.current = () => {
      setOpenColorPicker(null);
    };
  });

  useClickAway(colorPickerRef, (e) => {
    // Close the color picker on clicks inside the panel content
    // Without this check dragging the parent panel content will close the color picker and that doesn't seem intended.
    if (panelContentRef.current?.contains(e.target as Node)) {
      closeRef.current();
    }
  });

  const onResetTheme = () => {
    dispatchUserEditorAction({
      type: 'set-journey-color-settings',
      themeSettings: {
        ...getDefaultThemeSettings(),
        fontValues: themeSettings.fontValues,
      },
    });
  };

  const onSetColorThemeClick = (theme: CanvasUiColorTheme) => {
    trackSegmentEvent('Editor Theme Option Selected', { theme });
    let colorValues: ColorThemeValues;
    if (theme === 'custom') {
      const previousTheme = themeSettings.theme;
      if (previousTheme === 'custom') {
        colorValues = themeSettings.colorValues;
      } else if (previousTheme === 'default') {
        colorValues = DEFAULT_THEME_COLOR_VALUES;
      } else {
        colorValues = getColorThemeValuesFromColor(themeSettings.brandColor, previousTheme);
      }
    } else if (theme === 'default') {
      colorValues = DEFAULT_THEME_COLOR_VALUES;
    } else {
      colorValues = getColorThemeValuesFromColor(themeSettings.brandColor, theme);
    }
    dispatchUserEditorAction({
      type: 'set-journey-color-settings',
      themeSettings: {
        theme,
        brandColor: themeSettings.brandColor,
        colorValues,
        fontValues: themeSettings.fontValues,
      },
    });
  };

  const updateColorSettingCustomValue = (key: CustomValueKey, value: string, transient: boolean) => {
    dispatchUserEditorAction(
      {
        type: 'set-journey-color-settings',
        themeSettings: {
          theme: 'custom',
          brandColor: themeSettings.brandColor,
          colorValues: {
            ...themeSettings.colorValues,
            [key]: value,
          },
          fontValues: themeSettings.fontValues,
        },
      },
      {
        undoable: !transient,
        syncable: !transient,
      }
    );
  };

  const updateBrandColorValue = (value: string, transient: boolean) => {
    const theme = themeSettings.theme === 'default' ? 'light' : themeSettings.theme;
    dispatchUserEditorAction(
      {
        type: 'set-journey-color-settings',
        themeSettings: {
          theme,
          brandColor: value,
          colorValues: themeSettings.colorValues,
          fontValues: themeSettings.fontValues,
        },
      },
      {
        undoable: !transient,
        syncable: !transient,
      }
    );
  };

  const data = openColorPicker ? [openColorPicker] : [];

  const [transitions, colorPickerTransitionsApi] = useTransition<ColorPickerTypeKey, { y: number; opacity: number }>(
    data,
    () => ({
      key: (item: ColorPickerTypeKey) => item,
      from: (item: ColorPickerTypeKey) => {
        return {
          y: 8,
          opacity: 0,
        };
      },
      enter: (item: ColorPickerTypeKey) => {
        return {
          y: 0,
          opacity: 1,
          config: FLOAT_ANIMATION_CONFIG,
        };
      },
      leave: (item: ColorPickerTypeKey) => {
        return {
          y: 0,
          opacity: 0,
          config: { duration: 200, easing: easings.easeOutCubic },
        };
      },
    })
  );

  useEffect(() => {
    colorPickerTransitionsApi.start();
  }, [openColorPicker]);

  return (
    <>
      <ColorSettingInput
        key='brand'
        showInput
        label='Brand color'
        bold
        value={themeSettings.brandColor}
        onClick={() => {
          setOpenColorPicker('brand');
        }}
      />
      <div className='flex flex-col gap-2'>
        <span className='text-neue-journey-medium-strong text-neue-journey-fg'>Select theme</span>
        <div className='flex-1 flex gap-2'>
          <ControlPanelToggleButton
            selected={themeSettings.theme === 'light'}
            label='Light'
            icon={THEME_ICON_SVGS['light']}
            onClick={() => onSetColorThemeClick('light')}
          />
          <ControlPanelToggleButton
            selected={themeSettings.theme === 'dark'}
            label='Dark'
            icon={THEME_ICON_SVGS['dark']}
            onClick={() => onSetColorThemeClick('dark')}
          />
        </div>
        <div className='flex-1 flex gap-2'>
          <ControlPanelToggleButton
            selected={themeSettings.theme === 'bright'}
            label='Bright'
            icon={THEME_ICON_SVGS['bright']}
            onClick={() => onSetColorThemeClick('bright')}
          />
          <ControlPanelToggleButton
            selected={themeSettings.theme === 'custom'}
            label='Custom'
            icon={THEME_ICON_SVGS['custom']}
            onClick={() => onSetColorThemeClick('custom')}
          />
        </div>
      </div>
      {themeSettings.theme === 'custom' && (
        <div className='flex flex-col gap-2'>
          <ColorSettingInput
            key='background'
            label='Background'
            value={themeSettings.colorValues.background}
            onClick={() => {
              setOpenColorPicker('background');
            }}
          />
          <ColorSettingInput
            key='text'
            label='Text'
            value={themeSettings.colorValues.text}
            onClick={() => {
              setOpenColorPicker('text');
            }}
          />
          <ColorSettingInput
            key='accents'
            label='Accents'
            value={themeSettings.colorValues.accents}
            onClick={() => {
              setOpenColorPicker('accents');
            }}
          />
          {/* <ColorSettingInput
            key='headings'
            label='Headings'
            value={themeSettings.colorValues.headings}
            onClick={() => {
              setOpenColorPicker('headings');
            }}
          />
          <ColorSettingInput
            key='accents'
            label='Paragraphs'
            value={themeSettings.colorValues.paragraphs}
            onClick={() => {
              setOpenColorPicker('paragraphs');
            }}
          /> */}
        </div>
      )}
      <ControlPanelActionButton label='Reset colors' iconComponent={RefreshLightIcon} onClick={onResetTheme} />
      <div className='absolute top-0 pointer-events-none' ref={colorPickerRef}>
        {transitions((style, item) => {
          let color = item === 'brand' ? themeSettings.brandColor : themeSettings.colorValues[item];
          return (
            <animated.div className='absolute top-0' style={style}>
              <div className='w-[252px] -translate-x-full -ml-6 pointer-events-auto'>
                <ColorPicker
                  color={color}
                  allowBrandFetch={item === 'brand'}
                  onChange={(hex) =>
                    item === 'brand'
                      ? updateBrandColorValue(hex, false)
                      : updateColorSettingCustomValue(item, hex, false)
                  }
                  onClose={() => setOpenColorPicker(null)}
                />
              </div>
            </animated.div>
          );
        })}
      </div>
    </>
  );
};
